import React, {useEffect, useState} from 'react'
import {Box, CircularProgress, IconButton, Typography} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import {Image} from 'antd'
import AppDialog from 'src/components-new/feedback/AppDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import PdfPreview from 'src/components-new/feedback/PdfPreview'
import Button from 'src/components-new/primitives/Button'
import {STATUS} from 'src/constants/constants'

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml']
const AUDIO_VIDEO_MIME_TYPES = ['audio/mpeg', 'video/x-msvideo', 'video/mp4', 'video/mpeg']

interface FilePreviewDialogProps {
  fileName: string
  filePath: string
  mime: string
  onClose: () => void
  open: boolean
  status: string
}

export default function FilePreviewDialog({
  fileName,
  filePath,
  mime,
  onClose,
  open,
  status,
}: FilePreviewDialogProps): JSX.Element {
  const [pageNumber, setPageNumber] = useState(1)
  const [numPages, setNumPages] = useState(0)
  const [pdfError, setPdfError] = useState(false)

  useEffect(() => {
    setPageNumber(1)
    setNumPages(0)
    setPdfError(false)
  }, [filePath])

  const handleDownload = () => {
    const a = document.createElement('a')
    a.href = filePath
    a.download = fileName
    a.click()
  }

  const downloadButton = filePath ? (
    <Button
      onClick={handleDownload}
      startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
      variant="primary"
    >
      {'Scarica'}
    </Button>
  ) : undefined

  const isPdf = mime === 'application/pdf'
  const showPager = isPdf && numPages > 1 && !pdfError

  const pager = showPager ? (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
      <IconButton
        aria-label="Pagina precedente"
        disabled={pageNumber <= 1}
        onClick={() => setPageNumber((page) => page - 1)}
        size="small"
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography aria-live="polite" color="text.secondary" variant="body2">
        {`Pagina ${pageNumber} di ${numPages}`}
      </Typography>
      <IconButton
        aria-label="Pagina successiva"
        disabled={pageNumber >= numPages}
        onClick={() => setPageNumber((page) => page + 1)}
        size="small"
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  ) : null

  let actions: React.ReactNode
  if (!downloadButton) {
    actions = undefined
  } else if (pager) {
    actions = (
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
        {pager}
        {downloadButton}
      </Box>
    )
  } else {
    actions = downloadButton
  }

  return (
    <AppDialog
      actions={actions}
      disableEnforceFocus
      maxWidth="lg"
      onClose={onClose}
      open={open}
      title={fileName || 'Anteprima'}
    >
      {status === STATUS.PENDING && (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress />
        </Box>
      )}
      {status === STATUS.FULFILLED && filePath && (
        <>
          {IMAGE_MIME_TYPES.includes(mime) && (
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
              <Image
                alt={fileName}
                preview={{zIndex: 9999}}
                src={filePath}
                style={{maxHeight: '70vh', maxWidth: '100%'}}
              />
            </Box>
          )}
          {isPdf && (
            pdfError ? (
              <InlineMessage severity="error">
                {"Non è stato possibile visualizzare l'anteprima del documento."}
              </InlineMessage>
            ) : (
              <PdfPreview
                fileName={fileName}
                filePath={filePath}
                onDocumentError={() => setPdfError(true)}
                onDocumentLoad={setNumPages}
                pageNumber={pageNumber}
              />
            )
          )}
          {AUDIO_VIDEO_MIME_TYPES.includes(mime) && (
            <Box component="iframe" src={filePath} sx={{border: 'none', height: '70vh', width: '100%'}} />
          )}
        </>
      )}
    </AppDialog>
  )
}
